'use client';

import { useState, useCallback } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Dimension =
  | 'Analytical'
  | 'Creative'
  | 'Social'
  | 'Enterprising'
  | 'Conventional'
  | 'Investigative';

interface AnswerOption {
  text: string;
  /** Points awarded to each dimension for selecting this answer */
  scores: Partial<Record<Dimension, number>>;
}

interface Question {
  id: number;
  question: string;
  subtitle: string;
  accent: string; // Tailwind color accent for the question card
  icon: string; // SVG path for the question category icon
  options: AnswerOption[];
}

interface PersonalityType {
  name: string;
  tagline: string;
  description: string;
  strengths: string[];
  challenges: string[];
  workEnvironment: string[];
  color: string; // Tailwind gradient classes
  icon: string; // SVG path
}

interface CareerRecommendation {
  title: string;
  description: string;
  fit: string; // e.g. "Strong fit" or "Great match"
}

interface QuizResult {
  primary: Dimension;
  secondary: Dimension;
  scores: Record<Dimension, number>;
  maxScore: number;
  careers: CareerRecommendation[];
}

/* ------------------------------------------------------------------ */
/*  Personality type data                                              */
/* ------------------------------------------------------------------ */

const PERSONALITY_TYPES: Record<Dimension, PersonalityType> = {
  Analytical: {
    name: 'The Analyst',
    tagline: 'Precision thinker. Data-driven decision maker.',
    description:
      'You thrive on logic, structure, and data. When others see chaos, you see patterns waiting to be decoded. You are methodical, detail-oriented, and most satisfied when you can break complex problems into solvable pieces. Your decisions are grounded in evidence, not gut feelings, and you have a natural ability to spot inconsistencies that others overlook.',
    strengths: [
      'Exceptional logical reasoning and problem decomposition',
      'Keen eye for detail and quality assurance',
      'Strong data interpretation and pattern recognition',
      'Objective and evidence-based decision making',
      'Natural ability to optimize systems and processes',
    ],
    challenges: [
      'May overanalyze and delay decisions (analysis paralysis)',
      'Can come across as overly critical or nitpicky',
      'May struggle with ambiguity when data is incomplete',
      'Risk of undervaluing emotional or intuitive input from others',
    ],
    workEnvironment: [
      'Structured environments with clear processes and metrics',
      'Roles that involve research, analysis, or quality control',
      'Teams that value precision and evidence-based approaches',
      'Access to data tools, dashboards, and analytical resources',
      'Quiet, focused workspaces with minimal interruptions',
    ],
    color: 'from-blue-500 to-cyan-500',
    icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  },
  Creative: {
    name: 'The Innovator',
    tagline: 'Visionary thinker. Boundary breaker.',
    description:
      'You see the world differently. Where others see limitations, you see possibilities. You are driven by originality, self-expression, and the need to create something meaningful. Routine bores you; novelty energizes you. You work best when given the freedom to experiment, iterate, and bring bold ideas to life without being boxed in by rigid processes.',
    strengths: [
      'Exceptional imagination and original thinking',
      'Strong visual and conceptual communication skills',
      'Ability to connect unrelated ideas into breakthrough solutions',
      'Natural comfort with ambiguity and experimentation',
      'Infectious enthusiasm that inspires creative collaboration',
    ],
    challenges: [
      'May struggle with repetitive tasks and rigid structure',
      'Can lose focus when juggling too many creative ideas',
      'Risk of prioritizing novelty over practical feasibility',
      'May resist following established processes or conventions',
    ],
    workEnvironment: [
      'Flexible schedules with room for creative exploration',
      'Collaborative, open-minded teams that welcome experimentation',
      'Access to design tools, prototyping resources, and creative spaces',
      'Roles that value originality and unconventional approaches',
      'Minimal bureaucracy and red tape around ideation',
    ],
    color: 'from-purple-500 to-pink-500',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  },
  Social: {
    name: 'The Connector',
    tagline: 'People-first leader. Empathetic communicator.',
    description:
      'You are energized by human connection. Your natural empathy and communication skills make you the person everyone turns to for advice, support, and collaboration. You believe that the best outcomes come from understanding people deeply and building genuine relationships. You excel at creating environments where others feel heard, valued, and motivated to contribute their best work.',
    strengths: [
      'Exceptional empathy and emotional intelligence',
      'Natural talent for mentoring, coaching, and teaching',
      'Strong conflict resolution and mediation skills',
      'Ability to build and maintain deep professional relationships',
      'Intuitive understanding of team dynamics and morale',
    ],
    challenges: [
      'May take on others\' problems as your own (compassion fatigue)',
      'Can struggle with giving tough, critical feedback',
      'Risk of avoiding conflict even when confrontation is necessary',
      'May prioritize harmony over making unpopular but correct decisions',
    ],
    workEnvironment: [
      'Collaborative, team-oriented cultures with open communication',
      'Roles that involve mentoring, training, or client relationships',
      'Organizations with a strong mission or social impact focus',
      'Regular face-to-face or video interaction with colleagues and clients',
      'Supportive management that values emotional intelligence',
    ],
    color: 'from-emerald-500 to-teal-500',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  },
  Enterprising: {
    name: 'The Driver',
    tagline: 'Bold strategist. Results-obsessed leader.',
    description:
      'You are wired for action, influence, and impact. Where others deliberate, you decide and move. You are a natural leader who thrives under pressure, embraces calculated risks, and measures success in tangible results. Whether building a business, leading a team, or negotiating a deal, you are most alive when the stakes are high and the outcome depends on your ability to execute.',
    strengths: [
      'Natural leadership and persuasion abilities',
      'Decisive under pressure with high risk tolerance',
      'Strong negotiation and stakeholder management skills',
      'Results-driven mindset with a bias toward action',
      'Ability to inspire and rally teams around ambitious goals',
    ],
    challenges: [
      'May move too fast and overlook important details',
      'Can be perceived as domineering or impatient',
      'Risk of burnout from constantly pushing for results',
      'May struggle with tasks that require patience without visible progress',
    ],
    workEnvironment: [
      'Fast-paced, competitive environments with clear goals and rewards',
      'Leadership roles with autonomy and decision-making authority',
      'Organizations that reward initiative and measurable results',
      'Opportunities for growth, promotion, and increasing responsibility',
      'Access to mentors and networks of ambitious professionals',
    ],
    color: 'from-orange-500 to-red-500',
    icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  },
  Conventional: {
    name: 'The Organizer',
    tagline: 'Master planner. Efficiency expert.',
    description:
      'You bring order to chaos. Your superpower is creating reliable systems, maintaining high standards, and ensuring nothing falls through the cracks. You value predictability, accuracy, and well-defined processes. Others may see administrative work as tedious, but you see it as the critical foundation that keeps everything running smoothly. Without people like you, organizations would collapse.',
    strengths: [
      'Exceptional organizational and planning abilities',
      'Meticulous attention to detail and accuracy',
      'Strong follow-through on commitments and deadlines',
      'Natural talent for creating and improving systems and processes',
      'Reliable, consistent, and dependable under all circumstances',
    ],
    challenges: [
      'May resist change or new approaches that disrupt routines',
      'Can become frustrated when others do not follow established processes',
      'Risk of focusing too much on how things are done vs. why',
      'May undervalue creative or unconventional solutions',
    ],
    workEnvironment: [
      'Well-structured organizations with clear roles and expectations',
      'Roles that involve planning, coordination, or compliance',
      'Teams that value reliability, consistency, and quality standards',
      'Access to project management tools and documentation systems',
      'Stable work environments without frequent, unpredictable changes',
    ],
    color: 'from-amber-500 to-yellow-500',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  },
  Investigative: {
    name: 'The Explorer',
    tagline: 'Curious mind. Deep thinker.',
    description:
      'You are driven by an insatiable curiosity to understand how things work at a fundamental level. Superficial answers frustrate you; you need to dig deeper. Whether it is a scientific question, a technical system, or a philosophical puzzle, you are happiest when you are learning something new and pushing the boundaries of your understanding. You are the person who reads the footnotes.',
    strengths: [
      'Deep intellectual curiosity and love of learning',
      'Ability to master complex technical or academic subjects',
      'Strong independent research and self-directed study skills',
      'Comfortable with abstract thinking and theoretical frameworks',
      'Persistent problem-solving even on difficult, long-term challenges',
    ],
    challenges: [
      'May struggle to communicate findings in simple, accessible language',
      'Can get lost in research and lose sight of practical application',
      'Risk of working in isolation and missing collaborative input',
      'May be reluctant to share incomplete work or unfinished ideas',
    ],
    workEnvironment: [
      'Intellectually stimulating environments that reward expertise',
      'Roles that involve research, investigation, or deep specialization',
      'Access to learning resources, conferences, and knowledge bases',
      'Autonomy to pursue questions and explore topics in depth',
      'Colleagues who value intellectual rigor and evidence-based thinking',
    ],
    color: 'from-indigo-500 to-violet-500',
    icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  },
};

/* ------------------------------------------------------------------ */
/*  Career recommendations by primary/secondary dimension               */
/* ------------------------------------------------------------------ */

const CAREER_MAP: Record<Dimension, CareerRecommendation[]> = {
  Analytical: [
    { title: 'Data Scientist', description: 'Turn massive datasets into actionable insights using statistics, machine learning, and visualization.', fit: 'Strong fit' },
    { title: 'Financial Analyst', description: 'Evaluate investments, forecast trends, and guide business decisions with financial modeling.', fit: 'Strong fit' },
    { title: 'Software Engineer', description: 'Design and build software systems, solving complex technical problems with clean, logical code.', fit: 'Strong fit' },
    { title: 'Management Consultant', description: 'Diagnose business problems, analyze data, and recommend strategic solutions for organizations.', fit: 'Great match' },
    { title: 'Actuary', description: 'Use mathematics and statistics to assess and manage financial risk for insurance and finance companies.', fit: 'Great match' },
    { title: 'Business Intelligence Analyst', description: 'Build dashboards and reports that transform raw data into strategic business insights.', fit: 'Strong fit' },
    { title: 'Cybersecurity Analyst', description: 'Protect systems by analyzing threats, identifying vulnerabilities, and implementing security measures.', fit: 'Great match' },
    { title: 'Quantitative Researcher', description: 'Develop mathematical models and algorithms for trading, pricing, or risk management.', fit: 'Strong fit' },
    { title: 'Quality Assurance Engineer', description: 'Design test strategies, automate testing, and ensure software meets rigorous quality standards.', fit: 'Great match' },
    { title: 'Operations Research Analyst', description: 'Use advanced analytics to help organizations make better decisions and optimize operations.', fit: 'Strong fit' },
  ],
  Creative: [
    { title: 'UX/UI Designer', description: 'Design intuitive, beautiful digital experiences that delight users and solve real problems.', fit: 'Strong fit' },
    { title: 'Creative Director', description: 'Lead creative teams to develop compelling campaigns, brands, and visual identities.', fit: 'Great match' },
    { title: 'Product Manager', description: 'Shape product vision, blend user research with creativity, and bring innovative products to life.', fit: 'Strong fit' },
    { title: 'Content Strategist', description: 'Craft content ecosystems that tell brand stories, drive engagement, and build loyal audiences.', fit: 'Great match' },
    { title: 'Architect', description: 'Design buildings and spaces that blend artistic vision with structural and environmental constraints.', fit: 'Strong fit' },
    { title: 'Marketing Strategist', description: 'Develop creative campaigns that cut through noise and connect brands with their audiences.', fit: 'Great match' },
    { title: 'Film/Video Producer', description: 'Bring stories to life through video, managing creative vision from concept to final cut.', fit: 'Strong fit' },
    { title: 'Brand Designer', description: 'Create visual identities that capture a brand essence and resonate across all touchpoints.', fit: 'Strong fit' },
    { title: 'Game Designer', description: 'Invent interactive worlds, design game mechanics, and create engaging player experiences.', fit: 'Great match' },
    { title: 'Innovation Consultant', description: 'Help organizations break free from conventional thinking and develop breakthrough products and services.', fit: 'Strong fit' },
  ],
  Social: [
    { title: 'Human Resources Manager', description: 'Build company culture, resolve workplace issues, and develop talent strategies that retain top performers.', fit: 'Strong fit' },
    { title: 'Therapist / Counselor', description: 'Help individuals navigate life challenges, mental health, and personal growth through professional guidance.', fit: 'Strong fit' },
    { title: 'Teacher / Professor', description: 'Shape minds, inspire curiosity, and help students develop skills that last a lifetime.', fit: 'Great match' },
    { title: 'Social Worker', description: 'Advocate for individuals and communities, connecting people with resources and support systems.', fit: 'Strong fit' },
    { title: 'Nonprofit Program Director', description: 'Lead social impact programs, manage teams, and drive measurable change in communities.', fit: 'Great match' },
    { title: 'Healthcare Administrator', description: 'Manage healthcare facilities and programs, balancing patient care quality with operational efficiency.', fit: 'Strong fit' },
    { title: 'Corporate Trainer', description: 'Design and deliver training programs that develop employee skills and improve team performance.', fit: 'Great match' },
    { title: 'Patient Advocate', description: 'Navigate healthcare systems on behalf of patients, ensuring they receive quality care and support.', fit: 'Strong fit' },
    { title: 'Community Manager', description: 'Build and nurture online or offline communities, fostering engagement and meaningful connections.', fit: 'Great match' },
    { title: 'Executive Coach', description: 'Guide leaders through career transitions, skill development, and performance improvement.', fit: 'Strong fit' },
  ],
  Enterprising: [
    { title: 'Startup Founder / CEO', description: 'Build a company from the ground up, making high-stakes decisions and leading teams through uncertainty.', fit: 'Strong fit' },
    { title: 'Sales Director', description: 'Lead revenue-generating teams, develop sales strategies, and close major deals that drive growth.', fit: 'Strong fit' },
    { title: 'Venture Capitalist', description: 'Evaluate startups, make investment decisions, and help portfolio companies scale rapidly.', fit: 'Great match' },
    { title: 'Business Development Manager', description: 'Identify growth opportunities, forge strategic partnerships, and expand market presence.', fit: 'Strong fit' },
    { title: 'Real Estate Developer', description: 'Identify properties, negotiate deals, and manage development projects from acquisition to completion.', fit: 'Great match' },
    { title: 'Political Campaign Manager', description: 'Develop campaign strategies, mobilize supporters, and navigate high-pressure political environments.', fit: 'Strong fit' },
    { title: 'Investment Banker', description: 'Advise on mergers, acquisitions, and capital raising, working on high-value financial transactions.', fit: 'Great match' },
    { title: 'Management Consultant (Strategy)', description: 'Advise C-suite executives on growth strategy, market entry, and competitive positioning.', fit: 'Strong fit' },
    { title: 'Product Marketing Manager', description: 'Drive product launches, competitive positioning, and go-to-market strategy to capture market share.', fit: 'Great match' },
    { title: 'Franchise Owner', description: 'Run your own business with a proven model, managing operations, staff, and local growth strategy.', fit: 'Strong fit' },
  ],
  Conventional: [
    { title: 'Project Manager', description: 'Plan, coordinate, and execute projects on time and within budget, keeping teams aligned and accountable.', fit: 'Strong fit' },
    { title: 'Accountant / CPA', description: 'Manage financial records, ensure regulatory compliance, and provide accurate financial reporting.', fit: 'Strong fit' },
    { title: 'Operations Manager', description: 'Streamline processes, manage resources, and ensure day-to-day operations run efficiently.', fit: 'Great match' },
    { title: 'Compliance Officer', description: 'Ensure organizations follow laws, regulations, and internal policies, managing risk and governance.', fit: 'Strong fit' },
    { title: 'Supply Chain Manager', description: 'Optimize logistics, inventory, and vendor relationships to ensure products move efficiently from source to customer.', fit: 'Great match' },
    { title: 'Database Administrator', description: 'Design, maintain, and optimize database systems that organizations depend on for critical data.', fit: 'Strong fit' },
    { title: 'Technical Writer', description: 'Create clear, structured documentation that makes complex systems and processes understandable.', fit: 'Great match' },
    { title: 'Financial Controller', description: 'Oversee financial reporting, budgeting, and internal controls for accuracy and regulatory compliance.', fit: 'Strong fit' },
    { title: 'Legal Paralegal', description: 'Research case law, prepare legal documents, and manage case files with precision and attention to detail.', fit: 'Great match' },
    { title: 'IT Systems Administrator', description: 'Maintain and optimize IT infrastructure, ensuring systems are reliable, secure, and well-documented.', fit: 'Strong fit' },
  ],
  Investigative: [
    { title: 'Research Scientist', description: 'Push the boundaries of human knowledge through hypothesis-driven experimentation and discovery.', fit: 'Strong fit' },
    { title: 'Machine Learning Engineer', description: 'Build intelligent systems that learn from data, solving complex problems with algorithms and AI.', fit: 'Strong fit' },
    { title: 'Medical Researcher', description: 'Conduct clinical or biomedical research to discover new treatments, therapies, and medical insights.', fit: 'Great match' },
    { title: 'University Professor', description: 'Combine deep research expertise with teaching, advancing knowledge while mentoring future experts.', fit: 'Strong fit' },
    { title: 'Economist', description: 'Analyze economic trends, model market behavior, and advise organizations or governments on policy.', fit: 'Great match' },
    { title: 'Forensic Analyst', description: 'Investigate digital or physical evidence using scientific methods to solve complex cases.', fit: 'Strong fit' },
    { title: 'Biostatistician', description: 'Apply statistical methods to biological and health data, designing studies and interpreting clinical results.', fit: 'Great match' },
    { title: 'R&D Engineer', description: 'Design experiments, develop prototypes, and push technical boundaries in product development labs.', fit: 'Strong fit' },
    { title: 'Policy Analyst', description: 'Research and evaluate public policies, analyzing data and evidence to recommend improvements.', fit: 'Great match' },
    { title: 'Astrophysicist / Physicist', description: 'Explore fundamental questions about the universe through theoretical and experimental physics.', fit: 'Strong fit' },
  ],
};

/* ------------------------------------------------------------------ */
/*  Quiz questions (15)                                                */
/* ------------------------------------------------------------------ */

const QUESTIONS: Question[] = [
  {
    id: 1,
    question: 'When you face a tough problem at work, what is your first instinct?',
    subtitle: 'Problem-solving approach',
    accent: 'border-blue-400 bg-blue-50',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    options: [
      { text: 'Break it down into data points and analyze each one systematically', scores: { Analytical: 3, Investigative: 1 } },
      { text: 'Brainstorm creative, unconventional solutions -- the wilder the better', scores: { Creative: 3, Investigative: 1 } },
      { text: 'Talk it through with teammates and get diverse perspectives', scores: { Social: 3, Enterprising: 1 } },
      { text: 'Take charge, make a quick decision, and iterate as you go', scores: { Enterprising: 3, Analytical: 1 } },
    ],
  },
  {
    id: 2,
    question: 'What kind of work environment makes you feel most energized?',
    subtitle: 'Work style preference',
    accent: 'border-emerald-400 bg-emerald-50',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    options: [
      { text: 'Quiet, focused space where I can do deep, uninterrupted work', scores: { Investigative: 3, Analytical: 1 } },
      { text: 'Flexible, open studio where ideas bounce around freely', scores: { Creative: 3, Social: 1 } },
      { text: 'Buzzing, social office where collaboration happens naturally', scores: { Social: 3, Enterprising: 1 } },
      { text: 'Structured office with clear processes and well-defined roles', scores: { Conventional: 3, Analytical: 1 } },
    ],
  },
  {
    id: 3,
    question: 'You just finished a big project. What are you most proud of?',
    subtitle: 'Motivation drivers',
    accent: 'border-purple-400 bg-purple-50',
    icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
    options: [
      { text: 'The elegant solution I designed -- it is technically beautiful', scores: { Analytical: 2, Investigative: 2 } },
      { text: 'The original idea that nobody else thought of', scores: { Creative: 3, Investigative: 1 } },
      { text: 'How well the team worked together to make it happen', scores: { Social: 3, Conventional: 1 } },
      { text: 'The measurable business impact -- revenue, growth, or recognition', scores: { Enterprising: 3, Analytical: 1 } },
    ],
  },
  {
    id: 4,
    question: 'How do you prefer to communicate complex ideas?',
    subtitle: 'Communication style',
    accent: 'border-orange-400 bg-orange-50',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    options: [
      { text: 'Charts, graphs, and data visualizations that speak for themselves', scores: { Analytical: 3, Conventional: 1 } },
      { text: 'Visual prototypes, sketches, or storytelling with compelling narratives', scores: { Creative: 3, Social: 1 } },
      { text: 'One-on-one conversations where I can read the room and adapt', scores: { Social: 3, Enterprising: 1 } },
      { text: 'Detailed documentation with clear structure and step-by-step logic', scores: { Conventional: 2, Investigative: 2 } },
    ],
  },
  {
    id: 5,
    question: 'A colleague disagrees with your approach. What do you do?',
    subtitle: 'Conflict and collaboration',
    accent: 'border-red-400 bg-red-50',
    icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    options: [
      { text: 'Present my data and evidence, then ask them to present theirs', scores: { Analytical: 2, Investigative: 2 } },
      { text: 'Get excited -- different perspectives often lead to better ideas', scores: { Creative: 3, Social: 1 } },
      { text: 'Listen carefully to understand their position, then find middle ground', scores: { Social: 3, Conventional: 1 } },
      { text: 'Debate it directly -- the best idea should win regardless of feelings', scores: { Enterprising: 3, Analytical: 1 } },
    ],
  },
  {
    id: 6,
    question: 'What would you most enjoy doing on a free afternoon at work?',
    subtitle: 'Natural interests',
    accent: 'border-teal-400 bg-teal-50',
    icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
    options: [
      { text: 'Diving into a research rabbit hole on something that fascinates me', scores: { Investigative: 3, Analytical: 1 } },
      { text: 'Sketching, designing, or working on a creative side project', scores: { Creative: 3, Investigative: 1 } },
      { text: 'Organizing a team lunch, mentoring a junior colleague, or networking', scores: { Social: 3, Enterprising: 1 } },
      { text: 'Cleaning up processes, organizing files, or optimizing a workflow', scores: { Conventional: 3, Analytical: 1 } },
    ],
  },
  {
    id: 7,
    question: 'How do you feel about taking risks in your career?',
    subtitle: 'Risk tolerance',
    accent: 'border-amber-400 bg-amber-50',
    icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    options: [
      { text: 'I prefer calculated risks backed by thorough research and data', scores: { Analytical: 2, Investigative: 2 } },
      { text: 'I love creative risks -- trying new things even if they might not work', scores: { Creative: 3, Enterprising: 1 } },
      { text: 'I take risks when I know my team supports me and we are in it together', scores: { Social: 2, Conventional: 2 } },
      { text: 'Big risks, big rewards -- I am comfortable betting on myself', scores: { Enterprising: 3, Creative: 1 } },
    ],
  },
  {
    id: 8,
    question: 'When learning something new, what approach works best for you?',
    subtitle: 'Learning style',
    accent: 'border-indigo-400 bg-indigo-50',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    options: [
      { text: 'Read the documentation, study the details, and practice methodically', scores: { Conventional: 2, Analytical: 2 } },
      { text: 'Jump in and experiment -- I learn by doing and making mistakes', scores: { Creative: 2, Enterprising: 2 } },
      { text: 'Find a mentor or study group and learn through discussion', scores: { Social: 3, Investigative: 1 } },
      { text: 'Deep-dive into the theory first so I understand the fundamentals', scores: { Investigative: 3, Analytical: 1 } },
    ],
  },
  {
    id: 9,
    question: 'Which of these tasks would you genuinely enjoy the most?',
    subtitle: 'Task preference',
    accent: 'border-cyan-400 bg-cyan-50',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    options: [
      { text: 'Building a financial model or analyzing a complex dataset', scores: { Analytical: 3, Conventional: 1 } },
      { text: 'Designing a new product feature or reimagining a user experience', scores: { Creative: 3, Investigative: 1 } },
      { text: 'Facilitating a workshop or coaching someone through a career decision', scores: { Social: 3, Creative: 1 } },
      { text: 'Pitching a new business idea or negotiating a major partnership', scores: { Enterprising: 3, Social: 1 } },
    ],
  },
  {
    id: 10,
    question: 'What frustrates you most at work?',
    subtitle: 'Stress and friction points',
    accent: 'border-rose-400 bg-rose-50',
    icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
    options: [
      { text: 'Decisions made without data or evidence to back them up', scores: { Analytical: 3, Investigative: 1 } },
      { text: 'Being forced to follow rigid processes with no room for creativity', scores: { Creative: 3, Enterprising: 1 } },
      { text: 'Working alone all day with no meaningful human interaction', scores: { Social: 3, Creative: 1 } },
      { text: 'Disorganization, missed deadlines, and lack of accountability', scores: { Conventional: 3, Enterprising: 1 } },
    ],
  },
  {
    id: 11,
    question: 'What does "success" mean to you?',
    subtitle: 'Values and priorities',
    accent: 'border-lime-400 bg-lime-50',
    icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    options: [
      { text: 'Mastering my craft and being recognized as an expert in my field', scores: { Investigative: 3, Analytical: 1 } },
      { text: 'Creating something original that makes people think differently', scores: { Creative: 3, Investigative: 1 } },
      { text: 'Making a meaningful difference in people\'s lives', scores: { Social: 3, Conventional: 1 } },
      { text: 'Building wealth, influence, and a legacy of achievement', scores: { Enterprising: 3, Conventional: 1 } },
    ],
  },
  {
    id: 12,
    question: 'You are leading a team meeting. How do you run it?',
    subtitle: 'Leadership style',
    accent: 'border-violet-400 bg-violet-50',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    options: [
      { text: 'Prepare a tight agenda with data and keep the discussion structured', scores: { Analytical: 2, Conventional: 2 } },
      { text: 'Start with an open brainstorm -- let ideas flow before narrowing down', scores: { Creative: 3, Social: 1 } },
      { text: 'Make sure everyone gets heard and feels comfortable contributing', scores: { Social: 3, Creative: 1 } },
      { text: 'Set clear goals, assign ownership, and drive toward decisions fast', scores: { Enterprising: 3, Conventional: 1 } },
    ],
  },
  {
    id: 13,
    question: 'What kind of content do you naturally gravitate toward?',
    subtitle: 'Intellectual curiosity',
    accent: 'border-sky-400 bg-sky-50',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    options: [
      { text: 'Scientific papers, research studies, and deep technical analyses', scores: { Investigative: 3, Analytical: 1 } },
      { text: 'Design portfolios, art exhibitions, and creative showcases', scores: { Creative: 3, Social: 1 } },
      { text: 'Biographies, interviews, and stories about human experiences', scores: { Social: 2, Investigative: 2 } },
      { text: 'Business news, market trends, and entrepreneur success stories', scores: { Enterprising: 3, Analytical: 1 } },
    ],
  },
  {
    id: 14,
    question: 'How do you handle a heavy workload with competing deadlines?',
    subtitle: 'Stress handling',
    accent: 'border-fuchsia-400 bg-fuchsia-50',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    options: [
      { text: 'Create a priority matrix, estimate time for each task, and execute in order', scores: { Conventional: 3, Analytical: 1 } },
      { text: 'Find creative shortcuts and figure out what I can combine or simplify', scores: { Creative: 2, Enterprising: 2 } },
      { text: 'Delegate, ask for help, and make sure the team shares the load', scores: { Social: 2, Enterprising: 2 } },
      { text: 'Focus intensely on the highest-impact item first and power through', scores: { Enterprising: 2, Analytical: 2 } },
    ],
  },
  {
    id: 15,
    question: 'If you could spend a year learning anything, what would it be?',
    subtitle: 'Dream pursuit',
    accent: 'border-green-400 bg-green-50',
    icon: 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5',
    options: [
      { text: 'Advanced mathematics, machine learning, or a scientific discipline', scores: { Investigative: 3, Analytical: 1 } },
      { text: 'Filmmaking, music production, or a new creative medium', scores: { Creative: 3, Social: 1 } },
      { text: 'Psychology, counseling, or a foreign language to connect with new cultures', scores: { Social: 3, Investigative: 1 } },
      { text: 'Entrepreneurship, investing, or strategic negotiation', scores: { Enterprising: 3, Conventional: 1 } },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Scoring logic                                                      */
/* ------------------------------------------------------------------ */

function computeResults(answers: Record<number, number>): QuizResult {
  const scores: Record<Dimension, number> = {
    Analytical: 0,
    Creative: 0,
    Social: 0,
    Enterprising: 0,
    Conventional: 0,
    Investigative: 0,
  };

  // Tally up scores from selected answers
  for (const question of QUESTIONS) {
    const selectedIndex = answers[question.id];
    if (selectedIndex === undefined) continue;
    const option = question.options[selectedIndex];
    for (const [dim, pts] of Object.entries(option.scores)) {
      scores[dim as Dimension] += pts;
    }
  }

  // Find primary and secondary dimensions
  const sorted = (Object.entries(scores) as [Dimension, number][]).sort(
    (a, b) => b[1] - a[1],
  );
  const primary = sorted[0][0];
  const secondary = sorted[1][0];
  const maxScore = sorted[0][1];

  // Build career recommendations: 7 from primary + 3 from secondary (deduped)
  const primaryCareers = CAREER_MAP[primary].slice(0, 7);
  const secondaryCareers = CAREER_MAP[secondary].filter(
    (c) => !primaryCareers.find((pc) => pc.title === c.title),
  ).slice(0, 3);
  const careers = [...primaryCareers, ...secondaryCareers];

  return { primary, secondary, scores, maxScore, careers };
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function DimensionBar({
  dimension,
  score,
  maxScore,
  isPrimary,
}: {
  dimension: Dimension;
  score: number;
  maxScore: number;
  isPrimary: boolean;
}) {
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const colors: Record<Dimension, string> = {
    Analytical: 'bg-blue-500',
    Creative: 'bg-purple-500',
    Social: 'bg-emerald-500',
    Enterprising: 'bg-orange-500',
    Conventional: 'bg-amber-500',
    Investigative: 'bg-indigo-500',
  };

  return (
    <div className="flex items-center gap-3">
      <span className={`w-28 text-sm font-semibold text-gray-700 shrink-0 ${isPrimary ? 'font-extrabold text-gray-900' : ''}`}>
        {dimension}
      </span>
      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ease-out ${colors[dimension]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-10 text-sm font-bold text-gray-600 text-right">{score}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function CareerQuizContent() {
  const [currentQuestion, setCurrentQuestion] = useState(-1); // -1 = intro screen
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const totalQuestions = QUESTIONS.length;
  const progress =
    currentQuestion >= 0
      ? Math.round(((currentQuestion + 1) / totalQuestions) * 100)
      : 0;

  const handleStartQuiz = useCallback(() => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    setCopied(false);
    setSelectedOption(null);
  }, []);

  const handleSelectAnswer = useCallback(
    (optionIndex: number) => {
      setSelectedOption(optionIndex);

      // Brief delay so the user can see their selection highlighted
      setTimeout(() => {
        const questionId = QUESTIONS[currentQuestion].id;
        const newAnswers = { ...answers, [questionId]: optionIndex };
        setAnswers(newAnswers);
        setSelectedOption(null);

        if (currentQuestion < totalQuestions - 1) {
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Final question answered -- compute results
          setIsCalculating(true);
          setTimeout(() => {
            setResult(computeResults(newAnswers));
            setIsCalculating(false);
          }, 1500);
        }
      }, 400);
    },
    [currentQuestion, answers, totalQuestions],
  );

  const handleRetake = useCallback(() => {
    setCurrentQuestion(-1);
    setAnswers({});
    setResult(null);
    setCopied(false);
    setSelectedOption(null);
  }, []);

  const handleCopyResults = useCallback(() => {
    if (!result) return;
    const pt = PERSONALITY_TYPES[result.primary];
    const st = PERSONALITY_TYPES[result.secondary];
    const text = [
      `My Career Personality Quiz Results`,
      ``,
      `Primary Type: ${pt.name} (${result.primary})`,
      `${pt.tagline}`,
      ``,
      `Secondary Type: ${st.name} (${result.secondary})`,
      ``,
      `Top Career Recommendations:`,
      ...result.careers.slice(0, 5).map((c, i) => `${i + 1}. ${c.title}`),
      ``,
      `Take the quiz: https://superpowerresume.com/tools/career-personality-quiz`,
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  }, [result]);

  // Option letter labels
  const optionLetters = ['A', 'B', 'C', 'D'];
  const optionColors = [
    'hover:border-blue-400 hover:bg-blue-50 focus:border-blue-400 focus:bg-blue-50',
    'hover:border-purple-400 hover:bg-purple-50 focus:border-purple-400 focus:bg-purple-50',
    'hover:border-emerald-400 hover:bg-emerald-50 focus:border-emerald-400 focus:bg-emerald-50',
    'hover:border-orange-400 hover:bg-orange-50 focus:border-orange-400 focus:bg-orange-50',
  ];
  const optionSelectedColors = [
    'border-blue-500 bg-blue-50 ring-2 ring-blue-200',
    'border-purple-500 bg-purple-50 ring-2 ring-purple-200',
    'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200',
    'border-orange-500 bg-orange-50 ring-2 ring-orange-200',
  ];
  const letterBgColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-emerald-500',
    'bg-orange-500',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <ToolHero
        title="Career Personality Quiz"
        description="Discover career paths that match your personality, strengths, and interests. 15 questions, zero sign-ups, instant results."
        category="Career Exploration"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* ============================================================ */}
        {/*  INTRO SCREEN                                                 */}
        {/* ============================================================ */}
        {currentQuestion === -1 && !result && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
                Find Your Ideal Career Path
              </h2>
              <p className="text-gray-600 max-w-lg mx-auto mb-8 leading-relaxed">
                Answer 15 quick questions about how you work, think, and solve problems. We will map your responses to six personality dimensions and recommend career paths that fit who you actually are -- not who you think you should be.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-indigo-600">15</p>
                  <p className="text-xs text-gray-500 font-medium">Questions</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-purple-600">3 min</p>
                  <p className="text-xs text-gray-500 font-medium">To Complete</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-pink-600">10+</p>
                  <p className="text-xs text-gray-500 font-medium">Career Matches</p>
                </div>
              </div>

              <button
                onClick={handleStartQuiz}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-lg cursor-pointer"
              >
                Start the Quiz
              </button>
              <p className="text-xs text-gray-400 mt-4">
                100% free. No sign-up required. Runs entirely in your browser.
              </p>
            </div>

            {/* What we measure */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Six Personality Dimensions We Measure
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {(Object.entries(PERSONALITY_TYPES) as [Dimension, PersonalityType][]).map(
                  ([dim, type]) => (
                    <div
                      key={dim}
                      className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div
                        className={`w-10 h-10 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center shrink-0`}
                      >
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={type.icon}
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{type.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{type.tagline}</p>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            <ToolCTA
              headline="Already know your career? Build a resume for it."
              description="Superpower Resume helps you craft a targeted, ATS-optimized resume for any career path in minutes."
              buttonText="Build My Resume"
            />
          </div>
        )}

        {/* ============================================================ */}
        {/*  QUIZ QUESTIONS                                               */}
        {/* ============================================================ */}
        {currentQuestion >= 0 && !result && !isCalculating && (
          <div>
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-600">
                  Question {currentQuestion + 1} of {totalQuestions}
                </span>
                <span className="text-sm font-bold text-indigo-600">{progress}%</span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question card */}
            {(() => {
              const q = QUESTIONS[currentQuestion];
              return (
                <div
                  key={q.id}
                  className={`bg-white rounded-2xl shadow-lg border-l-4 ${q.accent.split(' ')[0]} p-8`}
                >
                  {/* Category and icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-xl ${q.accent.split(' ')[1] || 'bg-gray-100'} flex items-center justify-center`}
                    >
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={q.icon}
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      {q.subtitle}
                    </span>
                  </div>

                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-8 leading-tight">
                    {q.question}
                  </h2>

                  {/* Options */}
                  <div className="space-y-3">
                    {q.options.map((option, idx) => {
                      const isSelected = selectedOption === idx;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleSelectAnswer(idx)}
                          disabled={selectedOption !== null}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer flex items-start gap-4 ${
                            isSelected
                              ? optionSelectedColors[idx]
                              : `border-gray-200 bg-white ${optionColors[idx]}`
                          } ${selectedOption !== null && !isSelected ? 'opacity-50' : ''}`}
                        >
                          <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold shrink-0 transition-colors ${
                              isSelected
                                ? `${letterBgColors[idx]} text-white`
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {optionLetters[idx]}
                          </span>
                          <span className="text-sm sm:text-base font-medium text-gray-800 leading-relaxed pt-1">
                            {option.text}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ============================================================ */}
        {/*  CALCULATING SCREEN                                           */}
        {/* ============================================================ */}
        {isCalculating && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center animate-pulse">
              <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Analyzing your personality...
            </h2>
            <p className="text-gray-500">
              Mapping your responses across 6 dimensions and finding your ideal career matches.
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/*  RESULTS                                                      */}
        {/* ============================================================ */}
        {result && (
          <div className="space-y-8">
            {/* Primary personality type */}
            {(() => {
              const pt = PERSONALITY_TYPES[result.primary];
              return (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div
                    className={`bg-gradient-to-r ${pt.color} px-8 py-10 text-white text-center`}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d={pt.icon}
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-white/80 uppercase tracking-wider mb-1">
                      Your Primary Type
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-extrabold mb-2">
                      {pt.name}
                    </h2>
                    <p className="text-lg text-white/90 font-medium">{pt.tagline}</p>
                  </div>
                  <div className="px-8 py-6">
                    <p className="text-gray-700 leading-relaxed">{pt.description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Secondary type */}
            {(() => {
              const st = PERSONALITY_TYPES[result.secondary];
              return (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 flex items-center gap-5">
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${st.color} flex items-center justify-center shrink-0`}
                  >
                    <svg
                      className="w-7 h-7 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d={st.icon}
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">
                      Secondary Type
                    </p>
                    <p className="text-lg font-extrabold text-gray-900">{st.name}</p>
                    <p className="text-sm text-gray-600">{st.tagline}</p>
                  </div>
                </div>
              );
            })()}

            {/* Personality breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Your Personality Breakdown
              </h3>
              <div className="space-y-4">
                {(Object.entries(result.scores) as [Dimension, number][])
                  .sort((a, b) => b[1] - a[1])
                  .map(([dim, score]) => (
                    <DimensionBar
                      key={dim}
                      dimension={dim}
                      score={score}
                      maxScore={result.maxScore}
                      isPrimary={dim === result.primary}
                    />
                  ))}
              </div>
            </div>

            {/* Top career recommendations */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Top 10 Career Paths For You
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Based on your {PERSONALITY_TYPES[result.primary].name} primary type
                and {PERSONALITY_TYPES[result.secondary].name} secondary type.
              </p>
              <div className="space-y-4">
                {result.careers.map((career, i) => (
                  <div
                    key={career.title}
                    className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all"
                  >
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-extrabold shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-gray-900">{career.title}</h4>
                        <span
                          className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            career.fit === 'Strong fit'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-indigo-100 text-indigo-700'
                          }`}
                        >
                          {career.fit}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                        {career.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strengths and challenges */}
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6">
                <h3 className="text-lg font-bold text-emerald-900 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Your Strengths
                </h3>
                <ul className="space-y-3">
                  {PERSONALITY_TYPES[result.primary].strengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-emerald-500 shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-emerald-800 leading-relaxed">
                        {s}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Challenges */}
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  Potential Challenges
                </h3>
                <ul className="space-y-3">
                  {PERSONALITY_TYPES[result.primary].challenges.map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-amber-500 shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01"
                        />
                      </svg>
                      <span className="text-sm text-amber-800 leading-relaxed">
                        {c}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Work environment */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                Ideal Work Environment
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Look for these qualities when evaluating jobs and companies:
              </p>
              <ul className="space-y-3">
                {PERSONALITY_TYPES[result.primary].workEnvironment.map((env, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-700 leading-relaxed">
                      {env}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions: Share + Retake */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleCopyResults}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:from-indigo-700 hover:to-purple-700 transition-all cursor-pointer"
              >
                {copied ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share Your Results
                  </>
                )}
              </button>

              <button
                onClick={handleRetake}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retake Quiz
              </button>
            </div>

            {/* Career Assessment Education */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Understanding Career Personality Assessments</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Career personality assessments are rooted in decades of occupational psychology research. This quiz draws on the framework established by John Holland&apos;s RIASEC model, which categorizes career interests into six types: Realistic, Investigative, Artistic, Social, Enterprising, and Conventional. Holland&apos;s theory, widely adopted by the U.S. Department of Labor, suggests that people are most satisfied and productive when their work environment aligns with their personality type.
                </p>
                <p>
                  The O*NET program, sponsored by the U.S. Department of Labor, uses Holland&apos;s framework to classify hundreds of occupations and map them to personality traits, work styles, and interest profiles. Tools like My Next Move help job seekers explore careers that match their interests using this same research-backed approach. Understanding your dominant work personality dimensions can help you target roles where you are most likely to thrive and avoid positions that lead to burnout or disengagement.
                </p>
                <p>
                  While no assessment can perfectly predict career success, research consistently shows that interest-job alignment is a strong predictor of job satisfaction. Use your results as a starting point for career exploration, not a definitive prescription. Combine these insights with informational interviews, job shadowing, and self-reflection to make informed career decisions.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET Online - Interest Profiler &amp; Career Exploration</a>
                  {' · '}
                  <a href="https://www.mynextmove.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">My Next Move (U.S. Department of Labor)</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop - Career Assessment Guide</a>
                </p>
              </div>
            </section>

            {/* CTA */}
            <ToolCTA
              headline="Found your career path? Build a resume for it."
              description="Superpower Resume creates targeted, ATS-optimized resumes tailored to your dream career. Start for free in minutes."
              buttonText="Build My Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
